import * as React from "react";

import type { Filter } from "./DataTableFilters";

export type DataTableSortDirection = "asc" | "desc";

/**
 * Imperative actions exposed by a mounted `DataTable.Table`. Obtain via
 * `useDataTableState().tableRef` or call the convenience wrappers on
 * `DataTableState` (e.g. `deselectAll()`).
 */
export interface DataTableHandle {
  deselectAll(): void;
}

export interface DataTableSort {
  columnId: string;
  direction: DataTableSortDirection;
}

export type DataTableLoadingState = "foreground" | "background" | "idle";

/**
 * Canonical page-size choices surfaced by the DataTable footer. Single source
 * of truth across all DataTable consumers and any saved-preferences UI that
 * surfaces a "rows per page" control. Don't fork this list per-domain.
 */
export const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

/**
 * Saved user preferences seeded into the table at mount. The wire shape
 * (`TableUIPreferences`) uses snake_case; this is the camelCase view consumers
 * receive from the per-domain preferences hooks (e.g. `useDatasetTablePreferences`).
 */
export interface DataTablePreferences {
  columns?: string[];
  rowsPerPage?: number;
}

interface UseDataTableStateOptions {
  /**
   * Initial sort applied on first render. Captured once at mount; later
   * changes to this prop are ignored — the hook is the source of truth from
   * then on.
   */
  initialSort?: DataTableSort;

  /**
   * Saved user preferences (page size, column order). Captured once at
   * mount; consumers should fetch preferences before rendering the table
   * (e.g. via a parent suspense boundary) so they're available on first render.
   * Later changes to this prop are ignored.
   */
  preferences?: DataTablePreferences;

  /**
   * Fired when the user changes a persisted preference. Receives the full
   * merged preferences object — the hook tracks the latest emitted shape so
   * editing one dimension (e.g. page size) doesn't clobber a prior edit to
   * another (e.g. column order). The consumer is responsible for persisting.
   */
  onPreferencesChange?: (next: DataTablePreferences) => void;

  /**
   * Initial applied filters seeded into the hook at mount. Captured once;
   * later changes to this prop are ignored — the hook owns filter state from
   * then on, and consumers append to it via `onFiltersChange`. Use this for
   * restoring saved or URL-encoded filter sets at initial render.
   */
  initialFilters?: Filter[];
}

/**
 * Bundle consumed by `<DataTable.Table>`. Spread it onto the component:
 * `<DataTable.Table {...tableState.table} ... />`.
 */
export interface DataTableSlice {
  /**
   * Initial sort applied to the table on first render. Read once at mount;
   * later changes are ignored — the table component is the source of truth
   * from then on, and `onSortChange` flows user-initiated sort changes back
   * to the hook.
   */
  initialSort: DataTableSort | null;
  /**
   * Initial column order applied to the table on first render. Read once
   * at mount; later changes are ignored — `onColumnOrderChange` flows
   * user-initiated re-ordering back to the hook.
   */
  initialColumnOrder: string[] | null;
  onSortChange: (sort: DataTableSort | null) => void;
  onColumnOrderChange: (order: string[]) => void;
  onSelectionChange: (ids: Set<string>) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  currentPage: number;
  nextPage: () => void;
  prevPage: () => void;
  canGetPrevPage: boolean;
  /** Populated by `DataTableTable` via `useImperativeHandle`. */
  tableRef?: React.RefObject<DataTableHandle>;
}

/**
 * Returned from `useDataTableState`. The top-level fields (`sort`, `pageSize`,
 * `selectedIds`, `currentPage`) are for the consumer to read — typically to
 * build queries, drive bulk-action UIs, or pass `currentPage` into a paginated
 * data hook. The `table` slice is a pre-shaped bundle meant to be spread onto
 * `<DataTable.Table>`; consumers shouldn't pluck individual fields out of it.
 *
 * `sort`, `pageSize`, and `currentPage` deliberately appear at top-level *and*
 * on the slice — same value, two audiences (consumer query-building vs the
 * table component).
 */
export interface DataTableState {
  sort: DataTableSort | null;
  pageSize: number;
  /**
   * Currently-selected row IDs (observed mirror of AG Grid's selection).
   * Empty unless `table` is spread onto `<DataTable.Table>`.
   */
  selectedIds: Set<string>;

  /**
   * Current page (zero-indexed)
   */
  currentPage: number;

  /**
   * Currently-applied filters. Read this to build the QuerySpec condition
   * (typically via `filterToCondition`).
   */
  filters: Filter[];

  /**
   * Appends a single filter to the applied list. Use this in
   * `<DataTable.Filters onFilterSelected>` — no need to read `filters` back
   * from state. Always resets `currentPage` to 0.
   */
  addFilter: (filter: Filter) => void;

  /**
   * Reset `currentPage` to 0. Already called internally on `sort`,
   * `pageSize`, and `filters` changes (any input owned by this hook that
   * affects the underlying query).
   */
  resetCurrentPage: () => void;

  /**
   * Clears all selected rows — updates the local `selectedIds` mirror and
   * calls `gridApi.deselectAll()` on the mounted table.
   */
  deselectAll: () => void;

  table: DataTableSlice;
}

export function useDataTableState(
  options: UseDataTableStateOptions = {},
): DataTableState {
  const {
    initialSort: initialSortOption,
    preferences,
    onPreferencesChange,
    initialFilters: initialFiltersOption,
  } = options;

  // Snapshot initial preferences once at mount
  const [snapshotPreferences] = React.useState<DataTablePreferences>(
    () => preferences ?? {},
  );

  // Snapshot of the initial sort, exposed to the table via the slice. Captured
  // once at mount; later user sorts are tracked separately in `sort` and
  // emitted via `onSortChange`.
  const [initialSort] = React.useState<DataTableSort | null>(
    initialSortOption ?? null,
  );
  const [sort, setSort] = React.useState<DataTableSort | null>(initialSort);
  const [pageSize, setPageSize] = React.useState(
    snapshotPreferences.rowsPerPage ?? 50,
  );

  const emptySelection = React.useMemo(() => new Set<string>(), []);
  const [selectedIds, setSelectedIds] =
    React.useState<Set<string>>(emptySelection);

  const tableRef = React.useRef<DataTableHandle>(null);

  const deselectAll = React.useCallback(() => {
    setSelectedIds(emptySelection);
    tableRef.current?.deselectAll();
  }, [emptySelection]);

  const [currentPage, setCurrentPage] = React.useState(0);

  // Snapshot of initial filters; the live `filters` array drives consumer
  // query-building. Snapshot mirrors `initialSort` above so late prop
  // changes can't reseed the filter state.
  const [initialFilters] = React.useState<Filter[]>(
    () => initialFiltersOption ?? [],
  );
  const [filters, setFilters] = React.useState<Filter[]>(initialFilters);

  const initialColumnOrder = snapshotPreferences.columns ?? null;

  // Track the latest emitted preferences so column-order edits and page-size
  // edits merge into each other rather than clobbering. Seeded from the mount
  // snapshot; updated whenever we fire `onPreferencesChange`.
  const latestPreferencesRef =
    React.useRef<DataTablePreferences>(snapshotPreferences);

  const emitPreferences = React.useCallback(
    (next: DataTablePreferences) => {
      latestPreferencesRef.current = next;
      onPreferencesChange?.(next);
    },
    [onPreferencesChange],
  );

  const resetCurrentPage = React.useCallback(() => {
    setCurrentPage(0);
    setSelectedIds(emptySelection);
  }, [emptySelection]);

  const onSortChange = React.useCallback(
    (next: DataTableSort | null) => {
      setSort(next);
      // Sort changes the underlying query, so reset to the first page.
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const onPageSizeChange = React.useCallback(
    (size: number) => {
      setPageSize(size);
      emitPreferences({
        ...latestPreferencesRef.current,
        rowsPerPage: size,
      });
      // Pagesize changes the underlying query, so reset to first page
      resetCurrentPage();
    },
    [emitPreferences, resetCurrentPage],
  );

  const nextPage = React.useCallback(() => {
    setCurrentPage((prev) => prev + 1);
  }, []);

  const prevPage = React.useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  const onSelectionChange = React.useCallback((ids: Set<string>) => {
    setSelectedIds(ids);
  }, []);

  const onColumnOrderChange = React.useCallback(
    (order: string[]) => {
      emitPreferences({
        ...latestPreferencesRef.current,
        columns: order,
      });
    },
    [emitPreferences],
  );

  const addFilter = React.useCallback(
    (filter: Filter) => {
      setFilters((prev) => [...prev, filter]);
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const canGetPrevPage = currentPage > 0;

  const table = React.useMemo<DataTableSlice>(
    () => ({
      initialSort,
      initialColumnOrder,
      onSortChange,
      onColumnOrderChange,
      onSelectionChange,
      pageSize,
      onPageSizeChange,
      currentPage,
      nextPage,
      prevPage,
      canGetPrevPage,
      tableRef,
    }),
    [
      initialSort,
      initialColumnOrder,
      onSortChange,
      onColumnOrderChange,
      onSelectionChange,
      pageSize,
      onPageSizeChange,
      currentPage,
      nextPage,
      prevPage,
      canGetPrevPage,
    ],
  );

  return React.useMemo<DataTableState>(
    () => ({
      sort,
      pageSize,
      selectedIds,
      currentPage,
      filters,
      addFilter,
      resetCurrentPage,
      deselectAll,
      table,
    }),
    [
      sort,
      pageSize,
      selectedIds,
      currentPage,
      filters,
      addFilter,
      resetCurrentPage,
      deselectAll,
      table,
    ],
  );
}
